import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Accounting, UnitDto } from '../../../context/GlobalContext'
import { EntityType } from './EntityField'
import { AccountingId } from '../../../config/finance/invoiceConfig'

type Props = {
    unit: UnitDto
    children: (tuple: DimensionFieldTuple) => ReactElement
    selectedEntityTypes?: EntityType[]
}

export type DimensionPivotValues = { [unitKey: string]: DimensionsDto }
export type DimensionsDto = {
    gla?: AccountingId
    costCenter?: AccountingId
    costUnit?: AccountingId
    customer?: AccountingId
    project?: AccountingId
    transaction?: AccountingId
}
export type DimensionFieldTuple = [EntityType, keyof DimensionsDto]

export const DimensionFieldMap = new Map<EntityType, keyof DimensionsDto>([
    [EntityType.GLA, 'gla'],
    [EntityType.CostCenter, 'costCenter'],
    [EntityType.CostUnit, 'costUnit'],
    [EntityType.Customer, 'customer'],
    [EntityType.Project, 'project'],
    [EntityType.Transaction, 'transaction'],
])
export const SupportedDimensionMap = new Map<EntityType, Accounting[]>([
    [EntityType.GLA, [Accounting.Exact, Accounting.Twinfield]],
    [EntityType.CostCenter, [Accounting.Exact, Accounting.Twinfield]],
    [EntityType.CostUnit, [Accounting.Exact]],
    [EntityType.Customer, [Accounting.Exact, Accounting.Twinfield]],
    [EntityType.Project, [Accounting.Exact, Accounting.Twinfield]],
    [EntityType.Transaction, [Accounting.Exact, Accounting.Twinfield]],
])

export default function DimensionFields({ unit, children, selectedEntityTypes }: Props): ReactElement {
    const unitDimensions = useMemo(() => {
        const result = unit?.dimensions || []
        result.push(EntityType.Customer)
        result.push(EntityType.Transaction)
        return result
    }, [unit])

    return (
        <>
            {[...DimensionFieldMap.entries()]
                .filter(([dimension]: DimensionFieldTuple) =>
                    SupportedDimensionMap.get(dimension).includes(unit.accounting),
                )
                .filter(
                    ([dimension]: DimensionFieldTuple) =>
                        !selectedEntityTypes || selectedEntityTypes.includes(dimension),
                )
                .filter(([dimension]: DimensionFieldTuple) => unitDimensions.includes(dimension))
                .map(children)}
        </>
    )
}
